import React, { useState, useEffect } from 'react';
import {
  Table, Card, Button, Modal, Form, Input, Select, Switch, Space, Tag,
  message, Popconfirm, Tooltip
} from 'antd';
import {
  PlusOutlined, EditOutlined, KeyOutlined, DeleteOutlined, ReloadOutlined,
  CrownOutlined, ReadOutlined
} from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const roleLabel = (role) => (role === 'admin' ? 'مدیر' : 'استاد');
const roleColor = (role) => (role === 'admin' ? 'gold' : 'blue');

export default function TeachersManager() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  const [editTarget, setEditTarget] = useState(null); // admin record
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const [resetTarget, setResetTarget] = useState(null); // admin record
  const [resetForm] = Form.useForm();
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAdmins();
      if (res.data.success) setAdmins(res.data.admins);
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در بارگذاری لیست حساب‌های ادمین/استاد');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      const res = await adminAPI.createAdminAccount(values);
      if (res.data.success) {
        message.success('حساب جدید ساخته شد');
        setCreateOpen(false);
        createForm.resetFields();
        load();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در ساخت حساب');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (record) => {
    setEditTarget(record);
    editForm.setFieldsValue({
      username: record.username,
      email: record.email,
      phoneNumber: record.phoneNumber || '',
      role: record.role,
      isActive: record.isActive
    });
  };

  const handleEdit = async (values) => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const res = await adminAPI.updateAdminAccount(editTarget._id, values);
      if (res.data.success) {
        message.success('حساب به‌روزرسانی شد');
        setEditTarget(null);
        load();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در ویرایش حساب');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (values) => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const res = await adminAPI.resetAdminPassword(resetTarget._id, values.newPassword);
      if (res.data.success) {
        message.success('رمز عبور این حساب بازنشانی شد');
        setResetTarget(null);
        resetForm.resetFields();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در بازنشانی رمز عبور');
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const res = await adminAPI.deleteAdminAccount(record._id);
      if (res.data.success) {
        message.success('حساب حذف شد');
        load();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در حذف حساب');
    }
  };

  const columns = [
    {
      title: 'نقش',
      dataIndex: 'role',
      render: (role) => (
        <Tag icon={role === 'admin' ? <CrownOutlined /> : <ReadOutlined />} color={roleColor(role)}>
          {roleLabel(role)}
        </Tag>
      ),
      filters: [{ text: 'مدیر', value: 'admin' }, { text: 'استاد', value: 'teacher' }],
      onFilter: (value, record) => record.role === value
    },
    { title: 'نام کاربری', dataIndex: 'username' },
    { title: 'ایمیل', dataIndex: 'email' },
    { title: 'شماره تماس', render: (_, r) => r.phoneNumber || '-' },
    {
      title: 'دانش‌آموز لینک‌شده',
      render: (_, r) => {
        if (!r.linkedUserId) return '-';
        const u = r.linkedUserId;
        return (
          <Tooltip title="این حساب از تبدیل یک دانش‌آموز ساخته شده — حساب دانش‌آموزی او همچنان فعال است">
            <span>{u.farsiFirstName || u.firstName} {u.farsiLastName || u.lastName} ({u.phoneNumber})</span>
          </Tooltip>
        );
      }
    },
    {
      title: 'فعال',
      render: (_, r) => (r.isActive ? <Tag color="green">فعال</Tag> : <Tag color="red">غیرفعال</Tag>)
    },
    {
      title: 'تاریخ ساخت',
      render: (_, r) => new Date(r.createdAt).toLocaleDateString('fa')
    },
    {
      title: 'عملیات',
      render: (_, record) => (
        <Space>
          <Tooltip title="ویرایش">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title="بازنشانی رمز عبور">
            <Button size="small" icon={<KeyOutlined />} onClick={() => setResetTarget(record)} />
          </Tooltip>
          <Popconfirm
            title="این حساب حذف شود؟"
            description={record.linkedUserId ? 'حساب دانش‌آموزی این شخص حذف نمی‌شود، فقط دسترسی استاد او لغو می‌شود.' : undefined}
            okText="حذف"
            cancelText="انصراف"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ fontFamily: "'Vazirmatn', sans-serif" }}>
      <Card
        title="مدیریت حساب‌های ادمین و استاد"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load}>تازه‌سازی</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              ساخت حساب جدید
            </Button>
          </Space>
        }
      >
        <p style={{ color: '#888', marginBottom: 16 }}>
          از اینجا می‌توانید مستقیماً حساب استاد یا ادمین جدید بسازید. برای تبدیل یک دانش‌آموز موجود به استاد،
          از صفحه‌ی «کاربران» و دکمه‌ی «تبدیل به استاد» روی همان کاربر استفاده کنید — در آن حالت حساب دانش‌آموزی
          دست‌نخورده باقی می‌ماند و فرد می‌تواند هم به‌عنوان دانش‌آموز و هم به‌عنوان استاد وارد شود.
        </p>

        <Table
          columns={columns}
          dataSource={admins}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      {/* ساخت حساب جدید */}
      <Modal
        title="ساخت حساب ادمین/استاد جدید"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        onOk={() => createForm.submit()}
        confirmLoading={creating}
        okText="ساخت حساب"
        cancelText="انصراف"
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="username" label="نام کاربری" rules={[{ required: true, message: 'نام کاربری الزامی است' }]}>
            <Input style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item name="email" label="ایمیل" rules={[{ required: true, message: 'ایمیل الزامی است' }, { type: 'email', message: 'ایمیل نامعتبر است' }]}>
            <Input style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item name="phoneNumber" label="شماره تماس (اختیاری)">
            <Input style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item name="password" label="رمز عبور" rules={[{ required: true, message: 'رمز عبور الزامی است' }, { min: 6, message: 'حداقل ۶ کاراکتر' }]}>
            <Input.Password style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item name="role" label="نقش" initialValue="teacher" rules={[{ required: true }]}>
            <Select options={[{ label: 'استاد', value: 'teacher' }, { label: 'مدیر', value: 'admin' }]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ویرایش حساب */}
      <Modal
        title={editTarget ? `ویرایش حساب — ${editTarget.username}` : ''}
        open={!!editTarget}
        onCancel={() => setEditTarget(null)}
        onOk={() => editForm.submit()}
        confirmLoading={saving}
        okText="ذخیره"
        cancelText="انصراف"
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="username" label="نام کاربری" rules={[{ required: true }]}>
            <Input style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item name="email" label="ایمیل" rules={[{ required: true }, { type: 'email' }]}>
            <Input style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item name="phoneNumber" label="شماره تماس">
            <Input style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item name="role" label="نقش" rules={[{ required: true }]}>
            <Select options={[{ label: 'استاد', value: 'teacher' }, { label: 'مدیر', value: 'admin' }]} />
          </Form.Item>
          <Form.Item name="isActive" label="فعال" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* بازنشانی رمز عبور */}
      <Modal
        title={resetTarget ? `بازنشانی رمز عبور — ${resetTarget.username}` : ''}
        open={!!resetTarget}
        onCancel={() => { setResetTarget(null); resetForm.resetFields(); }}
        onOk={() => resetForm.submit()}
        confirmLoading={resetting}
        okText="بازنشانی"
        cancelText="انصراف"
      >
        <Form form={resetForm} layout="vertical" onFinish={handleReset}>
          <Form.Item name="newPassword" label="رمز عبور جدید" rules={[{ required: true, message: 'رمز جدید الزامی است' }, { min: 6, message: 'حداقل ۶ کاراکتر' }]}>
            <Input.Password style={{ direction: 'ltr' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
